import { isNil } from 'lodash';
import { useCallback, useContext } from 'react';
import { useEntitySelection } from '@shared/api/providers/entitySelection';
import { ActionSheetOption } from '@shared/components/ActionSheet';
import { CardChannelContext } from '@shared/components/blocks/contexts/channel';
import { useToast } from '@shared/components/toast';
import { Entity, toCreateWorkflowEntity } from '@shared/data/entity/Entity';
import { useDuplicateEntities } from '@shared/scenes/cards/entityBulkActions/hooks';
import { useDeleteEntitiesMutation } from '@shared/scenes/cards/entityBulkActions/useDeleteWorkflowEntities';
import { API } from '@shared/types/api';
import { useBoolean } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { useNavigator } from '@shared/util/navigation';
import { ChannelPermissionsContext } from '@shared/util/permissions/ChannelPermissionsContext';

interface OverflowMenuHooks {
  shouldShowOverflowMenu: boolean;
  isMenuOpen: boolean;
  openMenu: VoidFunction;
  closeMenu: VoidFunction;
  menuOptions: ActionSheetOption[];
  isDeleteConfirmOpen: boolean;
  closeDeleteConfirm: VoidFunction;
  confirmDeleteOptions: ActionSheetOption[];
  isShareSheetOpen: boolean;
  closeShareSheet: VoidFunction;
  isDownloadSheetOpen: boolean;
  closeDownloadSheet: VoidFunction;
}

interface Props {
  entity: Entity;
  onPressOpenEntity: VoidFunction;
  workflowTemplate: API.WorkflowTemplateFragment;
}

export function useOverflowMenu({
  entity,
  workflowTemplate,
  onPressOpenEntity,
}: Props): OverflowMenuHooks {
  const workflowTemplateId = workflowTemplate?.id;
  const duplicateEntities = useDuplicateEntities({ workflowTemplateId });
  const { canCreateCard, canUpdateCard, canDeleteCard } = useContext(ChannelPermissionsContext);
  const entitySelection = useEntitySelection();
  const { navigate } = useNavigator();
  const channel = useContext(CardChannelContext);
  const channelSlug = channel?.slug;
  const isNoCodeWorkspace = !isNil(channel?.workflowTemplateId);

  const [isMenuOpen, openMenu, closeMenu] = useBoolean(false);
  const [isDeleteConfirmOpen, openDeleteConfirm, closeDeleteConfirm] = useBoolean(false);
  const [isShareSheetOpen, openShareSheet, closeShareSheet] = useBoolean(false);
  const [isDownloadSheetOpen, openDownloadSheet, closeDownloadSheet] = useBoolean(false);

  const { confirmDeleteOptions } = useDeleteCardWithConfirmation({
    workflowTemplateId,
    entityId: entity.id,
    onClose: closeDeleteConfirm,
  });

  const menuOptions: ActionSheetOption[] = [
    {
      title: i18n('common.edit'),
      hidden: !canUpdateCard.enabled,
      onPress: onPressOpenEntity,
    },
    {
      title: isNoCodeWorkspace
        ? i18n('card.menu.actions.duplicateFrom')
        : i18n('card.menu.actions.duplicate'),
      hidden: !canCreateCard.enabled,
      onPress: () => {
        if (isNoCodeWorkspace) {
          navigate('CardCreate', {
            channelSlug: channelSlug ?? '',
            fromEntity: entity.id,
          });
        } else {
          const entityToCreate = toCreateWorkflowEntity(entity, workflowTemplate);
          duplicateEntities([entityToCreate]);
        }
      },
    },
    {
      title: i18n('common.share'),
      onPress: openShareSheet,
    },
    {
      title: i18n('card.menu.actions.selectMultiple'),
      onPress: () => {
        entitySelection.addEntity(entity);
      },
    },
    {
      title: i18n('common.download'),
      onPress: openDownloadSheet,
    },
    {
      title: i18n('card.menu.actions.cardHistory'),
      hidden: isNil(channelSlug),
      onPress: () => {
        navigate('CardHistory', { cardId: entity.id, channelSlug: channelSlug ?? '' });
      },
    },
    {
      title: i18n('common.delete'),
      destructive: true,
      hidden: !canDeleteCard.enabled,
      onPress: () => {
        closeMenu();
        openDeleteConfirm();
      },
    },
  ];

  return {
    shouldShowOverflowMenu: canCreateCard.enabled || canDeleteCard.enabled || canUpdateCard.enabled,
    isMenuOpen,
    openMenu,
    closeMenu,
    menuOptions,
    isDeleteConfirmOpen,
    closeDeleteConfirm,
    confirmDeleteOptions,
    isShareSheetOpen,
    closeShareSheet,
    isDownloadSheetOpen,
    closeDownloadSheet,
  };
}

function useDeleteCardWithConfirmation({
  workflowTemplateId,
  entityId,
  onClose,
}: {
  workflowTemplateId: string;
  entityId: string;
  onClose: VoidFunction;
}) {
  const deleteEntities = useDeleteEntitiesMutation(workflowTemplateId);
  const { show: showToast } = useToast();

  const remove = useCallback(async () => {
    try {
      await deleteEntities([entityId]);
      showToast({
        icon: 'check-filled',
        message: i18n('card.details.deleteCompletedNoName'),
      });
    } catch (error) {
      console.error(error);
      showToast({
        style: 'warning',
        message: i18n('errors.somethingWentWrong'),
      });
    }
  }, [deleteEntities, entityId, showToast]);

  const confirmDeleteOptions: ActionSheetOption[] = [
    {
      title: i18n('card.details.deleteConfirmation.confirm'),
      destructive: true,
      onPress: () => {
        remove();
        onClose();
      },
    },
    {
      title: i18n('common.cancel'),
      onPress: onClose,
    },
  ];

  return { confirmDeleteOptions };
}
